import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FaTimes, FaCheckCircle, FaExclamationCircle, FaCheck } from 'react-icons/fa';
import { getSubscriberProfile, getFamilyMembers, addFamilyMember, updateFamilyMember } from '../services/subscriberWalletService';
import {
  getAvailableSlotsForApplication,
  getVcSymptoms,
  createVcOnBehalf,
  cancelVcOnBehalf,
  getApplicationVcs,
} from '../services/doctorService';
import PatientSelector from '../components/subscriber/PatientSelector';
import { formatRelationshipForApi, formatGenderForApi, calculateAgeFromDob } from '../utils/subscriberHelpers';

/* --------------------------------------------------------------------- utils */

const MAX_SYMPTOMS = 3;
const SLOT_DURATION_MIN = 15;

// Build an IST-aware ISO string the backend (Asia/Kolkata) will accept.
// slotTime is "HH:MM" (24h) from the /available-slots payload.
const buildIstIso = (dateStr, slotTime) => `${dateStr}T${slotTime}:00+05:30`;

// Add N minutes to an "HH:MM" string, returning "HH:MM".
const addMinutes = (hhmm, minutes) => {
  const [h, m] = hhmm.split(':').map(Number);
  const total = h * 60 + m + minutes;
  const hh = String(Math.floor(total / 60) % 24).padStart(2, '0');
  const mm = String(total % 60).padStart(2, '0');
  return `${hh}:${mm}`;
};

const buildDateCard = (offsetDays = 0) => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + offsetDays);
  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return {
    iso,
    dayName: d.toLocaleDateString('en-IN', { weekday: 'short' }),
    dateNum: d.getDate(),
    monthName: d.toLocaleDateString('en-IN', { month: 'short' }),
  };
};


const DATE_CARDS = [buildDateCard(0), buildDateCard(1)];

const slotTimeBucket = (hhmm) => {
  const h = Number(hhmm.split(':')[0]);
  if (h < 12) return 'Morning';
  if (h < 17) return 'Afternoon';
  return 'Evening';
};

const to12h = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  const am = h < 12;
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${am ? 'AM' : 'PM'}`;
};

// Extract a human-readable message from whatever the backend threw.
// makeVcRequest stringifies dict/list payloads so they arrive here as err.message.
// Backend's `invalid_json(serializer)` emits a list of {key, value} pairs — pick
// the first `value` instead of showing raw JSON to the ops user.
const extractApiError = (err, fallback) => {
  if (!err) return fallback;
  if (typeof err === 'string') return err;
  const raw = err.message || err.toString?.() || fallback;
  if (typeof raw !== 'string') return fallback;
  // If the message is a JSON-encoded list of field errors, surface the first one.
  if (raw.startsWith('[') || raw.startsWith('{')) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed[0]?.value) return String(parsed[0].value);
      if (parsed?.value) return String(parsed.value);
      if (parsed?.error) return String(parsed.error);
      if (parsed?.detail) return String(parsed.detail);
      if (typeof parsed?.msg === 'string') return parsed.msg;
    } catch { /* fall through */ }
  }
  return raw;
};

/* ------------------------------------------------------------------ component */

const VirtualConsultationBooking = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromReschedule = location.state?.fromReschedule === true;

  const currentRole = (() => {
    const authRole = localStorage.getItem('authRole');
    const roleMapping = { wallet_superadmin: 'SuperAdmin', wallet_ops: 'Ops' };
    return roleMapping[authRole] || null;
  })();
  const canAccess = currentRole === 'Ops' || currentRole === 'SuperAdmin';

  const [mounted, setMounted] = useState(false);
  const [notification, setNotification] = useState(null);
  useEffect(() => { setMounted(true); }, []);
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 6000);
  };

  // Subscriber + members
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileError, setProfileError] = useState(null);
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [savingMember, setSavingMember] = useState(false);
  const isLoadingProfileRef = useRef(false);

  // Wizard state
  const [step, setStep] = useState(1); // 1=date/slot, 2=patient, 3=symptoms, 4=review, 5=success
  const [selectedDate, setSelectedDate] = useState(DATE_CARDS[0].iso);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotError, setSlotError] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null); // { start_time, end_time }

  const [selectedMemberId, setSelectedMemberId] = useState('');

  const [allSymptoms, setAllSymptoms] = useState([]);
  const [loadingSymptoms, setLoadingSymptoms] = useState(false);
  const [symptomSearch, setSymptomSearch] = useState('');
  const [selectedSymptomIds, setSelectedSymptomIds] = useState([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);

  // Pre-check: if this subscriber already has an active VC today (pending /
  // confirmed), the backend will reject a new booking. Rather than letting
  // ops walk through 4 steps to discover that at submit time, short-circuit
  // on mount and let them act on the existing VC directly.
  // Mirrors the subscriber-portal useActiveCard redirect.
  // "completed" is handled separately — today's slot is done, allow booking
  // for tomorrow with today's date card disabled.
  const [existingTodayVc, setExistingTodayVc] = useState(null);
  const [existingTomorrowVc, setExistingTomorrowVc] = useState(null);
  const [checkingExisting, setCheckingExisting] = useState(!fromReschedule);
  // Separate trackers so only the clicked button disables, not both.
  const [existingRescheduleId, setExistingRescheduleId] = useState(null);
  const [existingCancelId, setExistingCancelId] = useState(null);
  // ISO date strings (today/tomorrow) whose consultations are completed — those date cards are disabled
  const [disabledDates, setDisabledDates] = useState([]);

  /* ------------------------------------------------------ subscriber profile */
  useEffect(() => {
    const loadData = async () => {
      if (isLoadingProfileRef.current) return;
      try {
        setLoadingProfile(true);
        setProfileError(null);
        if (!id || !currentRole) {
          setProfileError('Invalid request');
          setLoadingProfile(false);
          return;
        }
        isLoadingProfileRef.current = true;
        const profileData = await getSubscriberProfile(id, currentRole);
        const src = profileData?.msg || profileData?.data || profileData;
        if (src && typeof src === 'object') {
          setProfile({
            id: src.id || id,
            personal: src.personal || {},
            plan: src.plan || src.personal_subscription || src.subscription_plan || '',
            status: src.status || '',
            ref_code: src.ref_code || '',
          });
        }
      } catch (err) {
        setProfileError(extractApiError(err, 'Failed to load subscriber profile'));
      } finally {
        setLoadingProfile(false);
        isLoadingProfileRef.current = false;
      }
    };
    loadData();
  }, [id, currentRole]);

  /* --------------------------------------------------------------- members */
  // Request active-only members from the backend. The serializer emits
  // `status: 'Active' | 'Inactive'` (string), not a boolean, so the previous
  // client-side filter was a no-op — all members leaked through. Server-side
  // filter via ?status=active is the contract-safe fix. Fixes AP-823 round 2.
  const fetchMembers = useCallback(async () => {
    if (!id || !currentRole) return;
    setLoadingMembers(true);
    try {
      const response = await getFamilyMembers(id, currentRole, { activeOnly: true });
      let list = [];
      if (response?.msg?.items && Array.isArray(response.msg.items)) list = response.msg.items;
      else if (Array.isArray(response?.msg)) list = response.msg;
      else if (Array.isArray(response)) list = response;
      // Normalize field names: API may return date_of_birth instead of dob
      const normalized = list.map((m) => {
        const dob = m.dob || m.date_of_birth || m.dateOfBirth || null;
        return {
          ...m,
          dob,
          age: m.age != null ? m.age : calculateAgeFromDob(dob),
        };
      });
      setMembers(normalized);
    } catch {
      setMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  }, [id, currentRole]);

  useEffect(() => { if (profile) fetchMembers(); }, [profile, fetchMembers]);

  /* --------------------------------- existing VC today (pre-book check) */
  const checkExistingVcToday = useCallback(async () => {
    if (!id || !currentRole) return;
    setCheckingExisting(true);
    try {
      const response = await getApplicationVcs(currentRole, id);
      const payload = response?.msg || response?.body || response;
      const list = Array.isArray(payload?.consultations) ? payload.consultations : [];
      const todayIso = DATE_CARDS[0].iso;
      const tomorrowIso = DATE_CARDS[1].iso;
      const relevantDates = new Set([todayIso, tomorrowIso]);
      const BLOCKING_STATUSES = new Set(['pending', 'confirmed', 'completed']);
      // contract: internalapi/views/user_type/shared/doctor/application_doctor_list.py:62-63
      // (start_at: ISO datetime, status: lowercase enum 'pending'|'confirmed'|'cancelled'|...)

      const foundDisabled = [];
      let foundActiveVc = null;
      let foundTomorrowVc = null;

      for (const c of list) {
        if (!c || !c.start_at) continue;
        const startDate = new Date(c.start_at);
        const dateIso = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
        if (!relevantDates.has(dateIso)) continue;
        const status = String(c.status || '').toLowerCase();
        if (!BLOCKING_STATUSES.has(status)) continue;
        if (status === 'completed') {
          if (!foundDisabled.includes(dateIso)) foundDisabled.push(dateIso);
        } else if (dateIso === todayIso) {
          // Today's active booking — show the short-circuit screen so ops can cancel/reschedule
          foundActiveVc = c;
        } else {
          // Tomorrow's active booking — capture to show detail screen, disable that date card
          foundTomorrowVc = c;
          if (!foundDisabled.includes(dateIso)) foundDisabled.push(dateIso);
        }
      }

      if (foundActiveVc) {
        setExistingTodayVc(foundActiveVc);
        setExistingTomorrowVc(null);
        setDisabledDates(foundDisabled); // preserve tomorrow's status so "Book for Tomorrow" knows
      } else if (foundTomorrowVc) {
        setExistingTodayVc(null);
        setExistingTomorrowVc(foundTomorrowVc);
        setDisabledDates(foundDisabled);
      } else {
        setExistingTodayVc(null);
        setExistingTomorrowVc(null);
        setDisabledDates(foundDisabled);
        if (foundDisabled.length > 0) {
          const firstAvailable = DATE_CARDS.find(card => !foundDisabled.includes(card.iso));
          if (firstAvailable) setSelectedDate(firstAvailable.iso);
        }
      }
    } catch (err) {
      // Non-fatal: if the pre-check fails, fall through and let the user
      // try the stepper. Backend will reject with the same validation error.
      setExistingTodayVc(null);
      setExistingTomorrowVc(null);
    } finally {
      setCheckingExisting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, currentRole]);

  useEffect(() => { if (profile && !fromReschedule) checkExistingVcToday(); }, [profile, checkExistingVcToday, fromReschedule]);

  const handleExistingCancel = async () => {
    if (!existingTodayVc) return;
    const confirmed = window.confirm(
      'Cancel this consultation? The subscriber and doctor will be notified.'
    );
    if (!confirmed) return;
    setExistingCancelId(existingTodayVc.id);
    try {
      await cancelVcOnBehalf(currentRole, id, existingTodayVc.id);
      showNotification('Consultation cancelled', 'success');
      // Re-check — today is now free, stepper will render.
      await checkExistingVcToday();
    } catch (err) {
      showNotification(extractApiError(err, 'Failed to cancel consultation'), 'error');
    } finally {
      setExistingCancelId(null);
    }
  };

  const handleExistingReschedule = async () => {
    if (!existingTodayVc) return;
    const confirmed = window.confirm(
      'Reschedule cancels this consultation and lets you book a new slot. Continue?'
    );
    if (!confirmed) return;
    setExistingRescheduleId(existingTodayVc.id);
    try {
      await cancelVcOnBehalf(currentRole, id, existingTodayVc.id, { asReschedule: true });
      showNotification('Previous consultation cancelled. Pick a new slot below.', 'success');
      await checkExistingVcToday();
    } catch (err) {
      showNotification(extractApiError(err, 'Failed to reschedule'), 'error');
      setExistingRescheduleId(null);
    }
  };

  const handleAddMember = async (memberData) => {
    setAddingMember(true);
    try {
      const payload = {
        name: memberData.name,
        relation: formatRelationshipForApi(memberData.relationship),
        gender: formatGenderForApi(memberData.gender),
        age: memberData.dob ? calculateAgeFromDob(memberData.dob) : memberData.age,
        dob: memberData.dob,
        contact_number: memberData.mobile,
        email: memberData.email,
      };
      await addFamilyMember(id, currentRole, payload);
      await fetchMembers();
      showNotification('Family member added', 'success');
      return true;
    } catch (err) {
      showNotification(extractApiError(err, 'Failed to add family member'), 'error');
      return false;
    } finally {
      setAddingMember(false);
    }
  };

  const handleEditMember = async (memberId, memberData) => {
    setSavingMember(true);
    try {
      const payload = {
        name: memberData.name,
        relation: formatRelationshipForApi(memberData.relationship),
        gender: formatGenderForApi(memberData.gender),
        age: memberData.dob ? calculateAgeFromDob(memberData.dob) : memberData.age,
        dob: memberData.dob,
        contact_number: memberData.mobile,
        email: memberData.email,
      };
      await updateFamilyMember(id, memberId, currentRole, payload);
      await fetchMembers();
      showNotification('Family member updated', 'success');
      return true;
    } catch (err) {
      showNotification(extractApiError(err, 'Failed to update family member'), 'error');
      return false;
    } finally {
      setSavingMember(false);
    }
  };

  /* ----------------------------------------------------------------- slots */
  useEffect(() => {
    const loadSlots = async () => {
      if (!selectedDate || !id || !currentRole) return;
      setLoadingSlots(true);
      setSlotError(null);
      setSlots([]);
      setSelectedSlot(null);
      try {
        const response = await getAvailableSlotsForApplication(currentRole, id, selectedDate);
        const payload = response?.msg || response?.body || response;
        const raw = Array.isArray(payload?.slots) ? payload.slots : [];

        // Past-slot filtering is done server-side in get_available_slots_for_date()
        // (loan-and-subscription#306). Kept just the "free_doctors > 0" guard
        // as a belt-and-suspenders defence against empty/malformed entries.
        const normalised = raw
          .filter((s) => s.start_time && s.free_doctors > 0)
          .map((s) => ({
            start_time: s.start_time,
            end_time: s.end_time || addMinutes(s.start_time, SLOT_DURATION_MIN),
            free_doctors: s.free_doctors,
          }))
          .sort((a, b) => a.start_time.localeCompare(b.start_time));
        setSlots(normalised);
      } catch (err) {
        setSlotError(extractApiError(err, 'Failed to load slots'));
      } finally {
        setLoadingSlots(false);
      }
    };
    loadSlots();
  }, [selectedDate, id, currentRole]);

  /* -------------------------------------------------------------- symptoms */
  useEffect(() => {
    if (step !== 3 || allSymptoms.length > 0) return;
    const loadSymptoms = async () => {
      setLoadingSymptoms(true);
      try {
        const response = await getVcSymptoms(currentRole);
        const payload = response?.msg || response?.body || response;
        const raw = Array.isArray(payload?.symptoms) ? payload.symptoms : [];
        setAllSymptoms(raw);
      } catch (err) {
        showNotification(extractApiError(err, 'Failed to load symptoms'), 'error');
      } finally {
        setLoadingSymptoms(false);
      }
    };
    loadSymptoms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const toggleSymptom = (symId) => {
    setSelectedSymptomIds((prev) => {
      if (prev.includes(symId)) return prev.filter((x) => x !== symId);
      if (prev.length >= MAX_SYMPTOMS) {
        showNotification(`Max ${MAX_SYMPTOMS} symptoms can be selected`, 'error');
        return prev;
      }
      return [...prev, symId];
    });
  };

  const filteredSymptoms = symptomSearch.trim()
    ? allSymptoms.filter((s) => s.name.toLowerCase().includes(symptomSearch.toLowerCase()))
    : allSymptoms;

  /* ------------------------------------------------------------ submission */
  const handleSubmit = async () => {
    if (!selectedSlot || !selectedMemberId) return;
    setIsSubmitting(true);
    try {
      const payload = {
        member_id: selectedMemberId,
        start_at: buildIstIso(selectedDate, selectedSlot.start_time),
        end_at: buildIstIso(selectedDate, selectedSlot.end_time),
        symptom_ids: selectedSymptomIds,
      };
      const response = await createVcOnBehalf(currentRole, id, payload);
      const body = response?.msg || response?.body || response;
      setBookingResult(body);
      setStep(5);
      showNotification('Consultation booked successfully', 'success');
    } catch (err) {
      showNotification(extractApiError(err, 'Failed to book consultation'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ---------------------------------------------------- guards / headers */

  if (!currentRole || !canAccess) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">This feature is only available for Ops and SuperAdmin roles.</p>
          <button
            onClick={() => navigate('/subscriber-wallet')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  if (loadingProfile) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600" />
      </div>
    );
  }

  if (profileError || !profile) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{profileError || 'Profile not found'}</p>
          <button
            onClick={() => navigate(`/subscriber-wallet/subscriber/${id}`)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Back to Subscriber
          </button>
        </div>
      </div>
    );
  }

  const selectedMember = members.find((m) => m.id === selectedMemberId);

  // Block step 2 → 3 if "self" member is missing age/dob (mirrors subscriber-portal validation).
  const selfMissingDetails = (() => {
    if (!selectedMember) return false;
    if ((selectedMember.relationship || '').toLowerCase() !== 'self') return false;
    const hasAge = selectedMember.age != null && parseInt(selectedMember.age, 10) >= 1;
    const hasDob = !!selectedMember.dob;
    const hasGender = !!selectedMember.gender;
    return !hasAge || !hasDob || !hasGender;
  })();

  const selectedSymptomNames = allSymptoms
    .filter((s) => selectedSymptomIds.includes(s.id))
    .map((s) => s.name);

  const Header = (
    <div className="flex items-center justify-between mb-4">
      <button
        onClick={() => navigate(`/subscriber-wallet/subscriber/${id}`)}
        className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
      >
        ← Back to Subscriber
      </button>
      <h1 className="text-xl font-semibold text-gray-900">Book Virtual Consultation</h1>
      <div className="w-32" />
    </div>
  );

  // AP-837 round 2: the subscriber-identity header (name + mobile) was removed
  // entirely — in the ops booking flow the subscriber is already identified by
  // the URL path (/subscriber-wallet/subscriber/<id>/virtual-consultation), so
  // restating their name and mobile above the stepper was pure redundancy. The
  // Stepper now sits directly under the Back/Title header.

  const Stepper = (
    <div className="flex items-center gap-2 mb-6">
      {['Date & Slot', 'Patient', 'Symptoms', 'Review'].map((label, idx) => {
        const n = idx + 1;
        const isDone = step > n;
        const isCurrent = step === n;
        const canJumpBack = n < step;
        return (
          <div key={label} className="flex items-center gap-2">
            <button
              type="button"
              onClick={canJumpBack ? () => setStep(n) : undefined}
              disabled={!canJumpBack}
              aria-current={isCurrent ? 'step' : undefined}
              aria-label={canJumpBack ? `Go back to ${label}` : label}
              className={`flex items-center gap-2 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 ${
                canJumpBack ? 'cursor-pointer hover:opacity-80' : 'cursor-default'
              }`}
            >
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${
                  isDone
                    ? 'bg-purple-600 text-white'
                    : isCurrent
                    ? 'bg-purple-100 text-purple-800 border border-purple-600'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                {isDone ? <FaCheck size={10} /> : n}
              </div>
              <span className={`text-sm ${isCurrent ? 'font-semibold text-purple-700' : 'text-gray-600'}`}>
                {label}
              </span>
            </button>
            {n < 4 && <div className="w-6 h-px bg-gray-300 mx-1" />}
          </div>
        );
      })}
    </div>
  );

  /* ------------------------------------------------------------- render */

  // Fresh check loading (pre-stepper). Blocks rendering the stepper until we
  // know whether an active VC already exists for today.
  if (checkingExisting && !bookingResult) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-8">
        <div className="max-w-4xl mx-auto">
          {Header}
          <div className="flex justify-center items-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600" />
          </div>
        </div>
      </div>
    );
  }

  // Existing-VC-today short-circuit. Mirrors the subscriber portal's
  // useActiveCard behaviour — if the subscriber already has an active VC
  // for today, show it here with cancel/reschedule actions instead of the
  // booking stepper.
  if (existingTodayVc && !bookingResult) {
    const vc = existingTodayVc;
    const vcStatus = String(vc.status || '').toLowerCase();
    const canAct = vcStatus === 'pending' || vcStatus === 'confirmed';
    const rescheduleBusy = existingRescheduleId === vc.id;
    const cancelBusy = existingCancelId === vc.id;
    const doctorName = vc.doctor_data
      ? `${vc.doctor_data.first_name || ''} ${vc.doctor_data.last_name || ''}`.trim()
      : '—';
    const patientName = vc.member_data?.name || '—';
    const symptomNames = Array.isArray(vc.symptoms_list)
      ? vc.symptoms_list.map((s) => s.name).filter(Boolean).join(', ')
      : '—';
    const timeLabel = vc.start_at
      ? new Date(vc.start_at).toLocaleTimeString('en-IN', {
          hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata',
        })
      : '—';
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-8">
        <div className="max-w-4xl mx-auto">
          {Header}
          <div className="bg-amber-50 border border-amber-300 rounded-md p-4 mb-4">
            <div className="font-semibold text-amber-900 mb-1">
              This subscriber already has a Virtual Consultation today.
            </div>
            <div className="text-sm text-amber-800">
              Only one active consultation is allowed at a time. Cancel or reschedule to change the slot.
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-md shadow-sm p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <div className="text-xs uppercase text-gray-500">Status</div>
                <div className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                  vcStatus === 'confirmed' ? 'bg-green-100 text-green-800'
                    : vcStatus === 'pending' ? 'bg-yellow-100 text-yellow-800'
                    : vcStatus === 'completed' ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100 text-gray-700'
                }`}>{vc.status}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-500">Source</div>
                <div className="text-sm mt-1">
                  {vc.source === 'ops' ? (
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                      Booked by Ops{vc.created_by_name ? ` — ${vc.created_by_name}` : ''}
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                      Self-booked
                    </span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-500">Time</div>
                <div className="text-sm font-medium mt-1">{timeLabel}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-500">Doctor</div>
                <div className="text-sm font-medium mt-1">{doctorName}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-500">Patient</div>
                <div className="text-sm font-medium mt-1">{patientName}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-500">Symptoms</div>
                <div className="text-sm font-medium mt-1">{symptomNames}</div>
              </div>
            </div>
            {canAct ? (
              <div className="flex gap-3">
                <button
                  onClick={handleExistingReschedule}
                  disabled={rescheduleBusy || vc.can_reschedule === false}
                  className="px-5 py-2 rounded-md text-sm font-medium bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {rescheduleBusy ? 'Rescheduling…' : 'Reschedule'}
                </button>
                <button
                  onClick={handleExistingCancel}
                  disabled={cancelBusy || vc.can_cancel === false}
                  className="px-5 py-2 rounded-md text-sm font-medium border border-red-600 text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {cancelBusy ? 'Cancelling…' : 'Cancel'}
                </button>
              </div>
            ) : (
              <div className="text-sm text-gray-500 italic">
                This consultation has already {vcStatus}. No action available.
              </div>
            )}
          </div>
          {notification && (
            <div className="fixed top-4 right-4 z-50 max-w-md">
              <div
                className={`flex items-start gap-2 px-4 py-3 rounded-md shadow-lg ${
                  notification.type === 'success'
                    ? 'bg-green-50 border border-green-200 text-green-800'
                    : 'bg-red-50 border border-red-200 text-red-800'
                }`}
              >
                {notification.type === 'success' ? (
                  <FaCheckCircle className="mt-0.5 flex-shrink-0" />
                ) : (
                  <FaExclamationCircle className="mt-0.5 flex-shrink-0" />
                )}
                <div className="text-sm">{notification.message}</div>
                <button
                  onClick={() => setNotification(null)}
                  className="ml-2 text-current opacity-60 hover:opacity-100"
                >
                  <FaTimes size={12} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Existing-VC-tomorrow short-circuit — show it just like today's screen.
  if (existingTomorrowVc && !existingTodayVc && !bookingResult) {
    const vc = existingTomorrowVc;
    const vcStatus = String(vc.status || '').toLowerCase();
    const canAct = vcStatus === 'pending' || vcStatus === 'confirmed';
    const tomorrowRescheduleBusy = existingRescheduleId === vc.id;
    const tomorrowCancelBusy = existingCancelId === vc.id;
    const doctorName = vc.doctor_data
      ? `${vc.doctor_data.first_name || ''} ${vc.doctor_data.last_name || ''}`.trim()
      : '—';
    const patientName = vc.member_data?.name || '—';
    const symptomNames = Array.isArray(vc.symptoms_list)
      ? vc.symptoms_list.map((s) => s.name).filter(Boolean).join(', ')
      : '—';
    const timeLabel = vc.start_at
      ? new Date(vc.start_at).toLocaleTimeString('en-IN', {
          hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata',
        })
      : '—';
    const handleTomorrowCancel = async () => {
      const confirmed = window.confirm(
        'Cancel this consultation? The subscriber and doctor will be notified.'
      );
      if (!confirmed) return;
      setExistingCancelId(vc.id);
      try {
        await cancelVcOnBehalf(currentRole, id, vc.id);
        showNotification('Consultation cancelled', 'success');
        await checkExistingVcToday();
      } catch (err) {
        showNotification(extractApiError(err, 'Failed to cancel consultation'), 'error');
      } finally {
        setExistingCancelId(null);
      }
    };
    const handleTomorrowReschedule = async () => {
      const confirmed = window.confirm(
        'Reschedule cancels this consultation and lets you book a new slot. Continue?'
      );
      if (!confirmed) return;
      setExistingRescheduleId(vc.id);
      try {
        await cancelVcOnBehalf(currentRole, id, vc.id, { asReschedule: true });
        showNotification('Previous consultation cancelled. Pick a new slot below.', 'success');
        await checkExistingVcToday();
      } catch (err) {
        showNotification(extractApiError(err, 'Failed to reschedule'), 'error');
        setExistingRescheduleId(null);
      }
    };
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-8">
        <div className="max-w-4xl mx-auto">
          {Header}
          <div className="bg-blue-50 border border-blue-300 rounded-md p-4 mb-4">
            <div className="font-semibold text-blue-900 mb-1">
              This subscriber has a Virtual Consultation booked for tomorrow.
            </div>
            <div className="text-sm text-blue-800">
              Only one active consultation is allowed at a time. Cancel or reschedule to change the slot.
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-md shadow-sm p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <div className="text-xs uppercase text-gray-500">Status</div>
                <div className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                  vcStatus === 'confirmed' ? 'bg-green-100 text-green-800'
                    : vcStatus === 'pending' ? 'bg-yellow-100 text-yellow-800'
                    : vcStatus === 'completed' ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100 text-gray-700'
                }`}>{vc.status}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-500">Source</div>
                <div className="text-sm mt-1">
                  {vc.source === 'ops' ? (
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                      Booked by Ops{vc.created_by_name ? ` — ${vc.created_by_name}` : ''}
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                      Self-booked
                    </span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-500">Time (Tomorrow)</div>
                <div className="text-sm font-medium mt-1">{timeLabel}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-500">Doctor</div>
                <div className="text-sm font-medium mt-1">{doctorName}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-500">Patient</div>
                <div className="text-sm font-medium mt-1">{patientName}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-500">Symptoms</div>
                <div className="text-sm font-medium mt-1">{symptomNames}</div>
              </div>
            </div>
            {canAct ? (
              <div className="flex gap-3">
                <button
                  onClick={handleTomorrowReschedule}
                  disabled={tomorrowRescheduleBusy || vc.can_reschedule === false}
                  className="px-5 py-2 rounded-md text-sm font-medium bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {tomorrowRescheduleBusy ? 'Rescheduling…' : 'Reschedule'}
                </button>
                <button
                  onClick={handleTomorrowCancel}
                  disabled={tomorrowCancelBusy || vc.can_cancel === false}
                  className="px-5 py-2 rounded-md text-sm font-medium border border-red-600 text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {tomorrowCancelBusy ? 'Cancelling…' : 'Cancel'}
                </button>
              </div>
            ) : (
              <div className="text-sm text-gray-500 italic">
                This consultation has already {vcStatus}. No action available.
              </div>
            )}
          </div>
          {notification && (
            <div className="fixed top-4 right-4 z-50 max-w-md">
              <div
                className={`flex items-start gap-2 px-4 py-3 rounded-md shadow-lg ${
                  notification.type === 'success'
                    ? 'bg-green-50 border border-green-200 text-green-800'
                    : 'bg-red-50 border border-red-200 text-red-800'
                }`}
              >
                {notification.type === 'success' ? (
                  <FaCheckCircle className="mt-0.5 flex-shrink-0" />
                ) : (
                  <FaExclamationCircle className="mt-0.5 flex-shrink-0" />
                )}
                <div className="text-sm">{notification.message}</div>
                <button
                  onClick={() => setNotification(null)}
                  className="ml-2 text-current opacity-60 hover:opacity-100"
                >
                  <FaTimes size={12} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8">
      <div className="max-w-4xl mx-auto">
        {Header}
        {step < 5 && Stepper}

        {step === 1 && (
          <div className="bg-white border border-gray-200 rounded-md shadow-sm p-4">
            <div className="font-semibold text-gray-900 mb-3">1. Select Time Slot</div>
            <div className="mb-4">
              <div className="text-xs uppercase text-gray-500 mb-2">Select Consultation Date</div>
              {disabledDates.length > 0 && disabledDates.length < DATE_CARDS.length && (
                <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800">
                  {disabledDates.includes(DATE_CARDS[0].iso)
                    ? "Today's consultation is complete. Select a slot for tomorrow."
                    : "Tomorrow's consultation is complete. Select a slot for today."}
                </div>
              )}
              {disabledDates.length >= DATE_CARDS.length ? (
                <p className="text-sm text-gray-500 py-2">
                  No dates available. Both today and tomorrow's consultations are complete.
                </p>
              ) : (
                <div className="flex gap-3">
                  {DATE_CARDS.map((card, idx) => {
                    const isDisabled = disabledDates.includes(card.iso);
                    const isSelected = !isDisabled && selectedDate === card.iso;
                    return (
                      <button
                        key={card.iso}
                        disabled={isDisabled}
                        onClick={() => {
                          if (isDisabled) return;
                          setSelectedDate(card.iso);
                          setSelectedSlot(null);
                        }}
                        className={`flex flex-col items-center justify-center w-[88px] h-[90px] rounded-xl border-[3px] transition-colors ${
                          isDisabled
                            ? 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed opacity-40'
                            : isSelected
                            ? 'border-purple-600 bg-purple-50 text-purple-700'
                            : 'border-gray-300 bg-white text-gray-600 hover:border-purple-400 hover:bg-purple-50'
                        }`}
                      >
                        <span className="text-[13px] font-medium">{isDisabled ? 'Booked' : idx === 0 ? 'Today' : 'Tomorrow'}</span>
                        <span className="text-[22px] font-semibold leading-tight my-0.5">{card.dateNum}</span>
                        <span className="text-[13px] font-medium">{card.monthName}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {slotError && (
              <div
                role="alert"
                className="flex items-start gap-2 mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3 3 0 01-1.745 2.723c-.15.067-.255.2-.255.32V11a1 1 0 11-2 0v-.055c0-.945.639-1.751 1.506-2.14A1 1 0 0010 7zm0 6a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <div className="font-medium">Could not load slots</div>
                  <div className="mt-0.5">{slotError}</div>
                </div>
              </div>
            )}
            {loadingSlots && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600" />
              </div>
            )}
            {!loadingSlots && !slotError && slots.length === 0 && (
              <div className="text-center text-gray-500 py-6">
                No slots available for this date. Try another day.
              </div>
            )}
            {!loadingSlots && slots.length > 0 && (
              ['Morning', 'Afternoon', 'Evening'].map((bucket) => {
                const bucketSlots = slots.filter((s) => slotTimeBucket(s.start_time) === bucket);
                if (bucketSlots.length === 0) return null;
                return (
                  <div key={bucket} className="mb-4">
                    <div className="text-sm font-medium text-gray-700 mb-2">{bucket}</div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {bucketSlots.map((s) => {
                        const isSelected =
                          selectedSlot &&
                          selectedSlot.start_time === s.start_time &&
                          selectedSlot.end_time === s.end_time;
                        return (
                          <button
                            key={`${s.start_time}-${s.end_time}`}
                            onClick={() => setSelectedSlot(s)}
                            className={`px-3 py-2 rounded-md border text-sm ${
                              isSelected
                                ? 'bg-purple-600 text-white border-purple-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {to12h(s.start_time)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}

            <div className="flex justify-end mt-4">
              <button
                disabled={!selectedSlot}
                onClick={() => setStep(2)}
                className={`px-5 py-2 rounded-md text-sm font-medium ${
                  selectedSlot
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="bg-white border border-gray-200 rounded-md shadow-sm p-4">
            <div className="font-semibold text-gray-900 mb-3">2. Select Patient</div>
            <PatientSelector
              members={members}
              loading={loadingMembers}
              value={selectedMemberId}
              onChange={setSelectedMemberId}
              onAddMember={handleAddMember}
              onEditMember={handleEditMember}
              addingMember={addingMember}
              savingMember={savingMember}
              mounted={mounted}
            />
            {selfMissingDetails && (
              <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
                <span>
                  <strong>Missing details for Self.</strong> Age, date of birth, and gender are required before booking.
                  Please edit the member record using the pencil icon above to fill in the missing details.
                </span>
              </div>
            )}
            <div className="flex justify-between mt-4">
              <button
                onClick={() => setStep(1)}
                className="px-5 py-2 rounded-md text-sm font-medium border border-gray-300 hover:bg-gray-50"
              >
                Back
              </button>
              <button
                disabled={!selectedMemberId || selfMissingDetails}
                onClick={() => setStep(3)}
                className={`px-5 py-2 rounded-md text-sm font-medium ${
                  selectedMemberId && !selfMissingDetails
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="bg-white border border-gray-200 rounded-md shadow-sm p-4">
            <div className="font-semibold text-gray-900 mb-3">
              3. Select Symptoms <span className="text-sm font-normal text-gray-500">(max {MAX_SYMPTOMS})</span>
            </div>
            <input
              type="text"
              placeholder="Search symptoms..."
              className="w-full mb-3 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
              value={symptomSearch}
              onChange={(e) => setSymptomSearch(e.target.value)}
            />
            {selectedSymptomIds.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {allSymptoms
                  .filter((s) => selectedSymptomIds.includes(s.id))
                  .map((s) => (
                    <button
                      key={s.id}
                      onClick={() => toggleSymptom(s.id)}
                      className="px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full hover:bg-purple-200"
                    >
                      {s.name} <FaTimes size={10} className="inline ml-1" />
                    </button>
                  ))}
              </div>
            )}
            {loadingSymptoms && (
              <div className="flex items-center justify-center py-6">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
              </div>
            )}
            {!loadingSymptoms && (
              <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md">
                {filteredSymptoms.length === 0 && (
                  <div className="text-center text-gray-500 py-4">No symptoms match.</div>
                )}
                {filteredSymptoms.map((s) => {
                  const isSelected = selectedSymptomIds.includes(s.id);
                  return (
                    <button
                      key={s.id}
                      onClick={() => toggleSymptom(s.id)}
                      className={`w-full text-left px-3 py-2 border-b last:border-b-0 flex items-center justify-between ${
                        isSelected ? 'bg-purple-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <span className="text-sm">{s.name}</span>
                      {isSelected && <FaCheck className="text-purple-600" size={12} />}
                    </button>
                  );
                })}
              </div>
            )}
            <div className="flex justify-between mt-4">
              <button
                onClick={() => setStep(2)}
                className="px-5 py-2 rounded-md text-sm font-medium border border-gray-300 hover:bg-gray-50"
              >
                Back
              </button>
              <button
                disabled={selectedSymptomIds.length === 0}
                onClick={() => setStep(4)}
                className={`px-5 py-2 rounded-md text-sm font-medium ${
                  selectedSymptomIds.length > 0
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="bg-white border border-gray-200 rounded-md shadow-sm p-4">
            <div className="font-semibold text-gray-900 mb-3">4. Review &amp; Confirm</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <div className="text-xs uppercase text-gray-500">Date</div>
                <div className="text-sm font-medium">
                  {new Date(selectedDate).toLocaleDateString('en-IN', {
                    weekday: 'long', day: 'numeric', month: 'short', year: 'numeric',
                  })}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-500">Time</div>
                <div className="text-sm font-medium">
                  {selectedSlot ? to12h(selectedSlot.start_time) : '—'}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-500">Patient</div>
                <div className="text-sm font-medium">{selectedMember?.name || '—'}</div>
                {selectedMember?.relation && (
                  <div className="text-xs text-gray-500">Relation: {selectedMember.relation}</div>
                )}
              </div>
              <div>
                <div className="text-xs uppercase text-gray-500">Symptoms</div>
                <div className="text-sm font-medium">{selectedSymptomNames.join(', ') || '—'}</div>
              </div>
            </div>
            {selectedMember && (selectedMember.relation || '').toLowerCase() !== 'self' && (
              <div className="bg-purple-50 border border-purple-200 rounded-md p-3 text-sm text-purple-900 mb-4">
                Booking on behalf of <strong>{selectedMember.name || 'this patient'}</strong>.
                The subscriber and the doctor will both be notified via WhatsApp.
                This VC will show up in the subscriber portal as a regular upcoming consultation.
              </div>
            )}
            <div className="flex justify-between">
              <button
                onClick={() => setStep(3)}
                disabled={isSubmitting}
                className="px-5 py-2 rounded-md text-sm font-medium border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-6 py-2 rounded-md text-sm font-medium bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
              >
                {isSubmitting ? 'Booking…' : 'Book Consultation'}
              </button>
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="bg-white border border-green-200 rounded-md shadow-sm p-6 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
              <FaCheckCircle className="text-green-600" size={36} />
            </div>
            <div className="text-2xl font-semibold text-gray-900 mb-2">Consultation Booked</div>
            <div className="text-gray-600 mb-4">
              The subscriber and doctor have been notified.
            </div>
            {bookingResult?.id && (
              <div className="text-xs text-gray-500 mb-3">Booking ID: {bookingResult.id}</div>
            )}
            {/* Only one VC per subscriber per day is allowed (enforced by
                assign_doctor_to_application). The "Book Another" button that
                used to live here always errored on submit — removed per AP-822. */}
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => { setBookingResult(null); setStep(1); checkExistingVcToday(); }}
                className="px-5 py-2 rounded-md bg-purple-600 text-white hover:bg-purple-700"
              >
                View Consultation
              </button>
            </div>
          </div>
        )}

        {notification && (
          <div className="fixed top-4 right-4 z-50 max-w-md">
            <div
              className={`flex items-start gap-2 px-4 py-3 rounded-md shadow-lg ${
                notification.type === 'success'
                  ? 'bg-green-50 border border-green-200 text-green-800'
                  : 'bg-red-50 border border-red-200 text-red-800'
              }`}
            >
              {notification.type === 'success' ? (
                <FaCheckCircle className="mt-0.5 flex-shrink-0" />
              ) : (
                <FaExclamationCircle className="mt-0.5 flex-shrink-0" />
              )}
              <div className="text-sm">{notification.message}</div>
              <button
                onClick={() => setNotification(null)}
                className="ml-2 text-current opacity-60 hover:opacity-100"
              >
                <FaTimes size={12} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VirtualConsultationBooking;
